<template>
  <div
    v-editable="blok"
    class="ProductListWithImage"
    :class="desktopWidth('full')"
  >
    <div v-if="uiStore.isMobile">
      <div
        v-if="products.length !== 1 && blok.imageOrVideo && blok.imageOrVideo.filename"
        class="w-full mb-4"
        :class="{
          'aspect-square': blok.ratioMobile === 'square'
        }"
      >
        <video
          v-if="checkIfVideoFile(blok.imageOrVideo.filename)"
          class="w-full mb-4"
          :class="[blok.imagePosition, {
            'h-full object-cover': blok.ratioMobile === 'square'
          }]"
          loop
          muted
          autoplay
          playsinline
          controls
        >
          <source :src="blok.imageOrVideo.filename" type="video/mp4">
        </video>

        <nuxt-img
          v-else
          :src="blok.imageOrVideo.filename"
          :alt="blok.imageOrVideo?.alt || ''"
          class="w-full h-full object-cover mb-4"
          :class="[blok.imagePosition, {
            'h-full object-cover': blok.ratioMobile !== 'square'
          }]"
          preset="standard"
          loading="lazy"
          sizes="mob375:100vw mob390:100vw mob414:100vw desk:758px"
        />
      </div>

      <QuickBuy
        v-if="products.length === 1"
        :manual-product="products[0]"
        :additional-image="blok.imageOrVideo && blok.imageOrVideo.filename ? {
          file: blok.imageOrVideo.filename,
          imagePosition: blok.imagePosition
        } : null"
      />

      <component
        :is="blok.mobileDesign === 'swiper' && products.length > 2 ? Swiper:'div'"
        v-else
        :class="{
          'grid grid-cols-2 gap-4': blok.mobileDesign !== 'swiper' || products.length <= 2
        }"
        :space-between="4"
        :slides-per-view="2.1"
        :modules="[SwiperPagination]"
        :pagination="{
          dynamicBullets: true,
          clickable: false,
        }"
      >
        <component
          :is="blok.mobileDesign === 'swiper' ? SwiperSlide:'div'"
          v-for="(product) in products"
          :key="product.partNo"
        >
          <ProductCard
            :product="product"
            :enable-quick-buy="true"
            design="standard"
            :placement="100"
          />
        </component>
      </component>
    </div>
    <!-- * * * * * * * * * * * * DESKTOP -->
    <div
      v-else
      class="desk:layout-container w-full grid grid-cols-12 gap-12 tabletPortraitOnly:block"
    >
      <div
        class="min-h-410"
        :class="{
          'col-span-8': products.length <= 1,
          'col-span-6': products.length === 2,
          'col-span-5': products.length === 3 || products.length >= 5,
          'col-span-7': products.length === 4,
        }"
        :style="imageStyle"
      >
        <div
          class="relative h-full w-full"
          :class="{
            'aspect-square': blok.ratio === 'square',
            'aspect-auto': blok.ratio === 'fit',
          }"
        >
          <div
            v-if="blok.imageOrVideo && blok.imageOrVideo.filename && checkIfVideoFile(blok.imageOrVideo.filename)"
            class="absolute w-full h-full"
          >
            <video
              class="w-full h-full object-cover"
              :class="blok.imagePosition"
              loop
              muted
              autoplay
              playsinline
              controls
            >
              <source :src="blok.imageOrVideo.filename" type="video/mp4">
            </video>
          </div>
          <div
            v-else-if="blok.imageOrVideo && blok.imageOrVideo.filename"
            class="absolute h-full w-full"
          >
            <nuxt-img
              :src="blok.imageOrVideo.filename"
              :alt="blok.imageOrVideo?.alt || ''"
              class="w-full h-full object-cover"
              :class="blok.imagePosition"
              preset="standard"
              loading="lazy"
              sizes="mob375:100vw mob390:100vw mob414:100vw desk:758px"
            />
          </div>
          <div v-else><!-- no image --></div>
        </div>
      </div>
      <div
        class="grid gap-12"
        :class="{
          'col-span-4': products.length <= 1,
          'col-span-6 grid-cols-2': products.length === 2,
          'col-span-7 grid-cols-3': products.length === 3 || products.length >= 5,
          'col-span-5 grid-cols-2': products.length === 4,
        }"
      >
        <div v-if="products.length === 1">
          <QuickBuy
            :manual-product="products[0]"
            :manual-width="428"
            :additional-image="blok.imageOrVideo && blok.imageOrVideo.filename ? {
              file: blok.imageOrVideo.filename,
              imagePosition: blok.imagePosition
            } : null"
          />
        </div>
        <ProductCard
          v-for="(product) in products"
          v-else
          :key="product.partNo"
          :class="{
            'first:-mb-32 last:-mb-32': products.length === 2,
            'first:-mb-32 even:-mb-32 last:-mb-32': products.length === 3,
            'odd:-mb-32 last:-mb-32': products.length === 4,
            'first:pb-32 even:-mb-32 odd:-mb-32 last:-mb-32': products.length === 5 || products.length >= 6,
          }"
          :product="product"
          :enable-quick-buy="true"
          design="standard"
          :placement="100"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  DatasourceObjectPosition,
  ProductSelector, StoryBlokAsset,
  StoryBlokPropBase
} from '~/constants/types/storyblok';
import useDesktopWidth from '~/composeables/useDesktopWidth';
import { watch } from 'vue';
import useStoryblokAdmin from '~/composeables/useStoryblokAdmin';
import useAlgoliaFetch from '~/composeables/useAlgoliaFetch';
import { useUiStore } from '~/store/ui';
import ProductCard from '~/components/product/ProductCard.vue';

import { checkIfVideoFile, convertObjectPositionToBgPosition, storyblokScaleImage } from '~/util/imageUtil';
import QuickBuy from '~/components/product/QuickBuy.vue';
import Product, { ProductModel } from '~/models/product';
import { useGlobalContentStore } from '~/store/globalContent';
const globalContent = useGlobalContentStore();

const { isInStoryblokAdmin } = useStoryblokAdmin();
const { getProductsByUrl, convertRawToProducts } = useAlgoliaFetch();

const uiStore = useUiStore();

const Swiper = resolveComponent('Swiper');
const SwiperSlide = resolveComponent('SwiperSlide');

type Props = StoryBlokPropBase & {
  products: ProductSelector[]; // 1-6
  imageOrVideo: StoryBlokAsset;
  ratio: '' | 'fit' | 'square';
  ratioMobile: '' | 'fit' | 'square';
  mobileDesign: '' | 'grid' | 'swiper';
  imagePosition: DatasourceObjectPosition;
}
const props = defineProps<{
  blok: Props
}>();

const { desktopWidth } = useDesktopWidth(props);

const imageStyle = computed(()=> {
  if (props.blok.imageOrVideo && props.blok.imageOrVideo.filename
      && !checkIfVideoFile(props.blok.imageOrVideo.filename)
      && props.blok.ratio !== 'square') {

    const url = storyblokScaleImage(props.blok.imageOrVideo.filename, 758);

    return {
      backgroundImage: `url(${url})`,
      backgroundSize: 'cover',
      backgroundPosition: convertObjectPositionToBgPosition(props.blok.imagePosition),
    };
  }
  return {};
});

const productsPartNo = computed(()=> {
  return props.blok.products.filter((f)=> f.product !== '').map((m) => m.product.product.url).filter((f) => f !== '');
});

const products = ref<ProductModel[]>([]);
const getProducts = async() => {
  if (productsPartNo.value.length) {
    const res = await getProductsByUrl(productsPartNo.value);
    if (res) {
      const splitProducts = convertRawToProducts(res.hits);
      products.value = splitProducts.map((m) => m.product);
    } else {
      console.log('@todo handle error');
    }
  } else {
    products.value = [];
  }
};
await getProducts();

if (isInStoryblokAdmin()) {
  watch(() => props.blok.products, async() => {
    await getProducts();
  });
}

const jsonLd = {
  '@context': 'https://schema.org/',
  '@type': 'ItemList',
  numberOfItems: products.value.length,
  itemListElement: products.value.map(productData => {
    const productItem = Product.create(productData);
    const activeVariant = productItem?.variants[0];
    return {
      '@type': 'Product',
      url: productItem.url,
      name: productItem.name,
      image: productItem.imageList,
      description: productItem.metaDescription,
      sku: productItem.partNo,
      mpn: productItem.partNo,
      brand: {
        '@type': 'Brand',
        name: productItem.brand.name,
      },
      offers: {
        '@type': 'Offer',
        url: productItem.url,
        priceCurrency: globalContent.getCurrency,
        itemCondition: 'https://schema.org/NewCondition',
        availability: activeVariant?.canAddToCart ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
      },
    };
  }),
} as any;
useJsonld(jsonLd);
</script>

<style lang="postcss">
</style>
